import React from "react";
import {css, cx} from "@panda/css";

import {AreaIcon, FloorUpIcon, PriceIcon, RoomsIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {GalleryPicturesGrid} from "@pg-mono/gallery";
import {numberFormat} from "@pg-mono/string-utils";

import {prettyScroll} from "../../styles/receipes/pretty_scroll";
import {useShouldShowPriceByOfferConfigAndUser} from "../hooks/use_should_show_price_by_offer_config_and_user";
import {OfferDisplayType} from "../offer/helpers/OfferDisplayType";
import {OfferType} from "../offer/types/OfferType";
import {getPropertyPlanImages} from "../property/helpers/get_property_plan_images";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {PropertyListPlanImage} from "./PropertyListPlanImage";

interface IProps {
    link: string;
    property: IPropertyListProperty;
    offer: {
        id: number;
        configuration: {
            display_type: OfferDisplayType;
            is_autoresponder_enabled: boolean;
            limited_presentation?: boolean;
            phone_clip: string | null;
        };
        geo_point: {
            coordinates: [number, number];
        };
        is_active?: boolean;
        is_phone_clip_enabled: boolean;
        name: string;
        type: OfferType;
        region: {
            id: number;
            full_name: string;
        };
        vendor: {
            id: number;
            logo_url: string | null;
            name: string;
            slug: string;
        };
    };
    className?: string;
}

export const PropertyListPlanModalContent: React.FC<IProps> = (props) => {
    const {property, offer, className} = props;

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const formatFloor = () => {
        if (property && property.floor != null) {
            if (property.floor === 0) {
                return "Parter";
            } else {
                return numberFormat(property.floor);
            }
        }
        return null;
    };

    const onPropertyPlanClick = () => {
        if (property?.plan) {
            window.open(property?.plan, "_blank");
        }
    };

    return (
        <div className={cx(className, planWrapper)}>
            {property.plan_image_pages.length <= 1 ? (
                <PropertyListPlanImage property={property} offerName={offer.name} />
            ) : (
                <div className={propertyPlanGallery}>
                    <GalleryPicturesGrid
                        getImageAlt={(index) => `rzut ${index + 1} w nieruchomości ${property.number} z inwestycji ${offer.name}`}
                        images={getPropertyPlanImages(property.plan_image_pages)}
                        onImageClick={onPropertyPlanClick}
                    />
                </div>
            )}

            <div className={propertyIconsHolder}>
                {property.rooms && (
                    <div className={propertyIconsItem}>
                        <RoomsIcon size="2" fill="#fff" />

                        <div className={propertyIconsItemText}>
                            <Text className={itemName} variant="body_copy_2">
                                Pokoje
                            </Text>
                            <Text variant="body_copy_2" strong>
                                {property.rooms}
                            </Text>
                        </div>
                    </div>
                )}

                {property.area && (
                    <div className={propertyIconsItem}>
                        <AreaIcon size="2" fill="#fff" />

                        <div className={propertyIconsItemText}>
                            <Text className={itemName} variant="body_copy_2">
                                Powierzchnia
                            </Text>
                            <Text variant="body_copy_2" strong>
                                {property.area} m<sup>2</sup>
                            </Text>
                        </div>
                    </div>
                )}

                {offer.type === OfferType.HOUSE
                    ? property.plot_area && (
                          <div className={propertyIconsItem}>
                              <AreaIcon size="2" fill="#fff" />

                              <div className={propertyIconsItemText}>
                                  <Text className={itemName} variant="body_copy_2">
                                      Powierzchnia działki
                                  </Text>
                                  <Text variant="body_copy_2" strong>
                                      {property.plot_area} m<sup>2</sup>
                                  </Text>
                              </div>
                          </div>
                      )
                    : property.floor !== null && (
                          <div className={propertyIconsItem}>
                              <FloorUpIcon size="2" fill="#fff" />

                              <div className={propertyIconsItemText}>
                                  <Text className={itemName} variant="body_copy_2">
                                      Piętro
                                  </Text>
                                  <Text variant="body_copy_2" strong>
                                      {formatFloor()}
                                  </Text>
                              </div>
                          </div>
                      )}

                {shouldShowPrice && property.price && (
                    <div className={propertyIconsItem}>
                        <PriceIcon size="2" fill="#fff" />

                        <div className={propertyIconsItemText}>
                            <Text className={itemName} variant="body_copy_2">
                                Cena
                            </Text>
                            <Text variant="body_copy_2" strong className="bt">
                                {numberFormat(property.price)} zł
                            </Text>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const propertyPlanGallery = css({
    ...prettyScroll
});

const planWrapper = css({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    overflow: "hidden",

    md: {
        px: 0,
        pb: 8,
        pt: 0
    }
});

const propertyIconsHolder = css({
    flexGrow: 1,
    flexShrink: 0,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    mt: 3,
    color: "var(--colors-white)",

    md: {
        mt: 4,
        pl: 2,
        justifyContent: "flex-start"
    }
});

const propertyIconsItem = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    md: {
        mr: 4,
        flexDirection: "row",

        "& > svg": {
            transform: "scale(3)"
        }
    }
});

const propertyIconsItemText = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    mr: 1,
    ml: 0,
    alignItems: "center",

    md: {
        ml: 2,
        mt: 0,
        alignItems: "flex-start"
    }
});

const itemName = css({
    color: "var(--colors-gray-100)"
});
import React from "react";
import {css, cx} from "@panda/css";

import {AreaIcon, FloorUpIcon, PriceIcon, RoomsIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {GalleryPicturesGrid} from "@pg-mono/gallery";
import {numberFormat} from "@pg-mono/string-utils";

import {prettyScroll} from "../../styles/receipes/pretty_scroll";
import {useShouldShowPriceByOfferConfigAndUser} from "../hooks/use_should_show_price_by_offer_config_and_user";
import {OfferDisplayType} from "../offer/helpers/OfferDisplayType";
import {OfferType} from "../offer/types/OfferType";
import {getPropertyPlanImages} from "../property/helpers/get_property_plan_images";
import {IPropertyListProperty} from "../property/types/PropertyListProperty";
import {PropertyListPlanImage} from "./PropertyListPlanImage";

interface IProps {
    link: string;
    property: IPropertyListProperty;
    offer: {
        id: number;
        configuration: {
            display_type: OfferDisplayType;
            is_autoresponder_enabled: boolean;
            limited_presentation?: boolean;
            phone_clip: string | null;
        };
        geo_point: {
            coordinates: [number, number];
        };
        is_active?: boolean;
        is_phone_clip_enabled: boolean;
        name: string;
        type: OfferType;
        region: {
            id: number;
            full_name: string;
        };
        vendor: {
            id: number;
            logo_url: string | null;
            name: string;
            slug: string;
        };
    };
    className?: string;
}

export const PropertyListPlanModalContent: React.FC<IProps> = (props) => {
    const {property, offer, className} = props;

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const formatFloor = () => {
        if (property && property.floor != null) {
            if (property.floor === 0) {
                return "Parter";
            } else {
                return numberFormat(property.floor);
            }
        }
        return null;
    };

    const onPropertyPlanClick = () => {
        if (property?.plan) {
            window.open(property?.plan, "_blank");
        }
    };

    return (
        <div className={cx(className, planWrapper)}>
            {property.plan_image_pages.length <= 1 ? (
                <PropertyListPlanImage property={property} offerName={offer.name} />
            ) : (
                <div className={propertyPlanGallery}>
                    <GalleryPicturesGrid
                        getImageAlt={(index) => `rzut ${index + 1} w nieruchomości ${property.number} z inwestycji ${offer.name}`}
                        images={getPropertyPlanImages(property.plan_image_pages)}
                        onImageClick={onPropertyPlanClick}
                    />
                </div>
            )}

            <div className={propertyIconsHolder}>
                {property.rooms && (
                    <div className={propertyIconsItem}>
                        <RoomsIcon size="2" fill="#fff" />

                        <div className={propertyIconsItemText}>
                            <Text className={itemName} variant="body_copy_2">
                                Pokoje
                            </Text>
                            <Text variant="body_copy_2" strong>
                                {property.rooms}
                            </Text>
                        </div>
                    </div>
                )}

                {property.area && (
                    <div className={propertyIconsItem}>
                        <AreaIcon size="2" fill="#fff" />

                        <div className={propertyIconsItemText}>
                            <Text className={itemName} variant="body_copy_2">
                                Powierzchnia
                            </Text>
                            <Text variant="body_copy_2" strong>
                                {property.area} m<sup>2</sup>
                            </Text>
                        </div>
                    </div>
                )}

                {offer.type === OfferType.HOUSE
                    ? property.plot_area && (
                          <div className={propertyIconsItem}>
                              <AreaIcon size="2" fill="#fff" />

                              <div className={propertyIconsItemText}>
                                  <Text className={itemName} variant="body_copy_2">
                                      Powierzchnia działki
                                  </Text>
                                  <Text variant="body_copy_2" strong>
                                      {property.plot_area} m<sup>2</sup>
                                  </Text>
                              </div>
                          </div>
                      )
                    : property.floor !== null && (
                          <div className={propertyIconsItem}>
                              <FloorUpIcon size="2" fill="#fff" />

                              <div className={propertyIconsItemText}>
                                  <Text className={itemName} variant="body_copy_2">
                                      Piętro
                                  </Text>
                                  <Text variant="body_copy_2" strong>
                                      {formatFloor()}
                                  </Text>
                              </div>
                          </div>
                      )}

                {shouldShowPrice && property.price && (
                    <div className={propertyIconsItem}>
                        <PriceIcon size="2" fill="#fff" />

                        <div className={propertyIconsItemText}>
                            <Text className={itemName} variant="body_copy_2">
                                Cena
                            </Text>
                            <Text variant="body_copy_2" strong className="bt">
                                {numberFormat(property.price)} zł
                            </Text>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const propertyPlanGallery = css({
    ...prettyScroll
});

const planWrapper = css({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    overflow: "hidden",

    md: {
        px: 0,
        pb: 8,
        pt: 0
    }
});

const propertyIconsHolder = css({
    flexGrow: 1,
    flexShrink: 0,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    mt: 3,
    color: "var(--colors-white)",

    md: {
        mt: 4,
        pl: 2,
        justifyContent: "flex-start"
    }
});

const propertyIconsItem = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    md: {
        mr: 4,
        flexDirection: "row",

        "& > svg": {
            transform: "scale(3)"
        }
    }
});

const propertyIconsItemText = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    mr: 1,
    ml: 0,
    alignItems: "center",

    md: {
        ml: 2,
        mt: 0,
        alignItems: "flex-start"
    }
});

const itemName = css({
    color: "var(--colors-gray-100)"
});
